<template>
    <SideDrawer :open="open" :title="title" :subtitle="subtitle" :busy="isSaving" @close="emit('close')">
        <form id="markup-drawer-form" class="markup-form" novalidate @submit.prevent="submit">
            <p class="markup-form__intro">{{ intro }}</p>

            <div v-for="field in fields" :key="field.level" class="field">
                <label class="field__label" :for="`markup-drawer-${field.level}`">{{ field.label }}, %</label>
                <div class="markup-form__control">
                    <input
                        :id="`markup-drawer-${field.level}`"
                        v-model="form[field.level]"
                        type="number"
                        min="0"
                        :max="MAX_MARKUP_PERCENT"
                        step="0.01"
                        inputmode="decimal"
                        :placeholder="field.placeholder"
                        :class="['form-input', 'markup-form__number', { 'is-invalid': errors[field.level] }]"
                        :disabled="isSaving || !field.isEditable"
                    />
                    <button
                        v-if="isCampaign && field.isEditable && form[field.level] !== ''"
                        type="button"
                        class="btn-link"
                        :disabled="isSaving"
                        @click="form[field.level] = ''"
                    >Как у партнёра</button>
                </div>
                <p v-if="errors[field.level]" class="field__error">{{ errors[field.level] }}</p>
                <p v-else class="field__hint">{{ field.hint }}</p>
            </div>

            <div class="markup-summary" role="status">
                <p class="markup-summary__title">{{ summary.title }}</p>
                <p class="markup-summary__value">+{{ formatPercent(summary.percent) }}</p>
                <p class="markup-summary__formula">{{ summary.formula }}</p>
            </div>

            <p v-if="generalError" class="form-error" role="alert">{{ generalError }}</p>
        </form>

        <template #footer>
            <button type="button" class="btn-secondary" :disabled="isSaving" @click="emit('close')">Отмена</button>
            <button type="submit" form="markup-drawer-form" class="btn-primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Сохранение…' : 'Сохранить' }}
            </button>
        </template>
    </SideDrawer>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import SideDrawer from '../../ui/SideDrawer.vue';
import { MARKUP_ENDPOINT, MARKUP_LEVEL, MARKUP_LEVEL_LABEL, MARKUP_TARGET, MAX_MARKUP_PERCENT } from '../../../constants/structure';
import { canEditMarkupLevel, compoundMarkupPercent, formatPercent, visibleMarkupLevels } from '../../../utils/markupUtils';
import { extractErrorMessage, extractFieldErrors } from '../../../utils/httpErrors';

const props = defineProps({
    open: { type: Boolean, required: true },
    target: { type: String, default: MARKUP_TARGET.PARTNER, validator: value => Object.values(MARKUP_TARGET).includes(value) },
    /** Партнёр или RTB-кампания из data.structure. */
    entity: { type: Object, default: null },
    /** Партнёр цели: даёт abilities и дефолты, которые наследует кампания. */
    partner: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);

const LEVEL_HINT = Object.freeze({
    [MARKUP_LEVEL.PLATFORM]: 'База SSP → цена агентства. Агентство эту наценку не видит.',
    [MARKUP_LEVEL.AGENCY]: 'Цена агентства → цена клиента. Задаётся агентством партнёра.',
});

const form = reactive({ [MARKUP_LEVEL.PLATFORM]: '', [MARKUP_LEVEL.AGENCY]: '' });
const errors = ref({});
const generalError = ref('');
const isSaving = ref(false);

const isCampaign = computed(() => props.target === MARKUP_TARGET.CAMPAIGN_RTB);
const markup = computed(() => props.entity?.markup || {});
const partnerMarkup = computed(() => props.partner?.markup || {});
const levels = computed(() => visibleMarkupLevels(isCampaign.value ? partnerMarkup.value : markup.value));

const toFormValue = value => (value == null ? '' : String(value));
const toPayloadValue = value => (value === '' ? null : Number(value));

watch(() => props.open, (isOpen) => {
    if (!isOpen) return;
    Object.values(MARKUP_LEVEL).forEach((level) => { form[level] = toFormValue(markup.value[level]); });
    errors.value = {};
    generalError.value = '';
});

const title = computed(() => (isCampaign.value ? 'Наценка кампании' : 'Наценки партнёра'));
const subtitle = computed(() => (isCampaign.value
    ? `${props.entity?.name || ''} · партнёр «${props.partner?.name || ''}»`
    : props.entity?.name || ''));

const intro = computed(() => (isCampaign.value
    ? 'Переопределяет наценку партнёра только для этой кампании. Пустое поле — наценка партнёра. Применяется к новым показам.'
    : 'Действует на все кампании партнёра, кроме тех, где задано переопределение. Применяется к новым показам.'));

const fields = computed(() => levels.value.map((level) => {
    const inherited = partnerMarkup.value[level] ?? 0;
    return {
        level,
        label: MARKUP_LEVEL_LABEL[level].full,
        isEditable: canEditMarkupLevel(props.partner, level),
        placeholder: isCampaign.value ? `${inherited} — как у партнёра` : '0',
        hint: isCampaign.value ? `У партнёра: ${formatPercent(inherited)}. ${LEVEL_HINT[level]}` : LEVEL_HINT[level],
    };
}));

/** Значение уровня с учётом наследования — для сводки. */
const effectiveValue = (level) => {
    if (form[level] !== '') return Number(form[level]);
    return Number(isCampaign.value ? partnerMarkup.value[level] ?? 0 : 0);
};

const summary = computed(() => {
    const agency = effectiveValue(MARKUP_LEVEL.AGENCY);
    if (!levels.value.includes(MARKUP_LEVEL.PLATFORM)) {
        return { title: 'Клиент платит сверх цены агентства', percent: agency, formula: 'Цена клиента = цена агентства × (1 + L2)' };
    }
    const platform = effectiveValue(MARKUP_LEVEL.PLATFORM);
    return {
        title: 'Итоговая наценка для клиента',
        percent: compoundMarkupPercent(platform, agency),
        formula: `Цена клиента = база × (1 + ${formatPercent(platform)}) × (1 + ${formatPercent(agency)})`,
    };
});

const validationError = (level) => {
    const value = form[level];
    if (value === '') {
        return !isCampaign.value && level === MARKUP_LEVEL.PLATFORM ? 'Укажите платформенную наценку' : '';
    }
    const number = Number(value);
    if (Number.isNaN(number)) return 'Наценка должна быть числом';
    if (number < 0) return 'Наценка не может быть отрицательной';
    if (number > MAX_MARKUP_PERCENT) return `Наценка не больше ${MAX_MARKUP_PERCENT} %`;
    return '';
};

const changedLevels = computed(() => fields.value
    .filter(field => field.isEditable)
    .map(field => field.level)
    .filter(level => toPayloadValue(form[level]) !== (markup.value[level] ?? null)));

const canSubmit = computed(() => changedLevels.value.length > 0);

const submit = async () => {
    if (!canSubmit.value || isSaving.value) return;

    errors.value = Object.fromEntries(changedLevels.value
        .map(level => [level, validationError(level)])
        .filter(([, message]) => message));
    if (Object.keys(errors.value).length) return;

    isSaving.value = true;
    generalError.value = '';
    try {
        const payload = Object.fromEntries(changedLevels.value.map(level => [level, toPayloadValue(form[level])]));
        const { data } = await axios.put(`${MARKUP_ENDPOINT}/${props.target}/${props.entity.id}`, payload);
        emit('saved', { target: props.target, entity: props.entity, markup: data.markup });
    } catch (error) {
        errors.value = extractFieldErrors(error);
        if (!Object.keys(errors.value).length) generalError.value = extractErrorMessage(error, 'Не удалось сохранить наценку');
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;
@use '../../../../scss/pages/admin/admin-form';

@include admin-form.fields;

.markup-form {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
}

.markup-form__intro {
    margin: 0;
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.markup-form__control {
    display: flex;
    align-items: center;
    gap: $spacing-md;
}

.markup-form__number {
    max-width: 200px;
}

.btn-link {
    padding: 0;
    border: none;
    background: none;
    font-size: $font-size-sm;
    color: $admin-primary-color;
    cursor: pointer;

    &:hover:not(:disabled) { text-decoration: underline; }
}

.markup-summary {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
    padding: $spacing-lg;
    border: 1px solid $admin-border-color;
    border-radius: $radius-md;
    background: $admin-bg-secondary;

    p { margin: 0; }
}

.markup-summary__title {
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.markup-summary__value {
    font-size: $font-size-3xl;
    font-weight: 600;
    color: $admin-text-primary;
}

.markup-summary__formula {
    font-size: $font-size-xs;
    color: $admin-text-muted;
}
</style>
